import * as React from 'react';
import debounce from 'lodash/debounce';
import { Input } from '@/components/ui/input';

interface SearchInputProps {
  /** Current committed value (from the URL). */
  value: string;
  /** Commit a new value — debounced internally; pass a stable callback. */
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  delay?: number;
}

/**
 * Debounced search box shared by the table toolbars. Keeps a responsive local
 * value while committing to the URL on a delay, and re-syncs when the committed
 * value changes externally (clear-all, browser back/forward).
 */
const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  className,
  delay = 500,
}) => {
  const [local, setLocal] = React.useState(value);

  React.useEffect(() => {
    setLocal(value);
  }, [value]);

  const debounced = React.useMemo(() => debounce(onChange, delay), [onChange, delay]);
  React.useEffect(() => () => debounced.cancel(), [debounced]);

  return (
    <Input
      placeholder={placeholder}
      className={className}
      autoComplete="off"
      value={local}
      onChange={(e) => {
        setLocal(e.target.value);
        debounced(e.target.value);
      }}
    />
  );
};

export default SearchInput;
